import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Icon from '../components/Icon';
import { fetchCareersTickets, fetchCareersTicket, patchCareersTicket } from '../services/api';
import { timeAgo } from '../utils/format';

// Application state machine. Order in tabs follows the funnel:
// open → first_contact → interview → closed_*
const STATES = [
  { id: 'open',            label: 'Open',            badge: 'badge-warning' },
  { id: 'first_contact',   label: 'First contact',   badge: 'badge-info'    },
  { id: 'interview',       label: 'Interview',       badge: 'badge-info'    },
  { id: 'closed_accepted', label: 'Closed · Accepted', badge: 'badge-success' },
  { id: 'closed_declined', label: 'Closed · Declined', badge: 'badge-muted'   },
];

const STATE_BY_ID = Object.fromEntries(STATES.map((s) => [s.id, s]));

const fmtAbs = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const TicketRow = ({ ticket, selected, onSelect }) => {
  const s = STATE_BY_ID[ticket.application_state] || STATES[0];
  return (
    <button
      type="button"
      onClick={() => onSelect(ticket)}
      style={{
        textAlign: 'left',
        width: '100%',
        background: selected ? 'var(--teal-faint)' : 'var(--bg-elevated)',
        border: `1px solid ${selected ? 'var(--teal)' : 'var(--bd)'}`,
        borderRadius: 'var(--r-md)',
        padding: 'var(--s3) var(--s4)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div className="row row-gap-3" style={{ justifyContent: 'space-between' }}>
        <div className="font-semibold" style={{ color: 'var(--tx1)', fontSize: 13 }}>
          {ticket.subject || '(no subject)'}
        </div>
        <span className={clsx('badge', s.badge)}>{s.label}</span>
      </div>
      <div className="text-xs text-tx3" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span>{ticket.from_name || ticket.from_email}</span>
        <span>{timeAgo(ticket.last_message_at)}</span>
      </div>
      {ticket.role_applied_for && (
        <div className="text-xs" style={{ color: 'var(--teal)' }}>{ticket.role_applied_for}</div>
      )}
      {ticket.last_message_preview && (
        <div className="text-xs text-tx3" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ticket.last_message_preview}
        </div>
      )}
    </button>
  );
};

const StateActions = ({ ticket, onMove, busy }) => {
  // Suggest the next logical state(s) given the current one. Always allow
  // every state as a fallback (e.g. correcting a misclick).
  return (
    <div className="row row-gap-2" style={{ flexWrap: 'wrap' }}>
      {STATES.map((s) => {
        const isCurrent = s.id === ticket.application_state;
        return (
          <button
            key={s.id}
            type="button"
            disabled={busy || isCurrent}
            onClick={() => onMove(s.id)}
            className={clsx('btn', isCurrent ? 'btn-ghost' : 'btn-outline')}
            style={{ padding: '6px 12px', fontSize: 12, opacity: isCurrent ? 0.5 : 1 }}
          >
            {isCurrent ? `· ${s.label}` : `→ ${s.label}`}
          </button>
        );
      })}
    </div>
  );
};

const Careers = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('open');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCareersTickets({ limit: 200 });
      setTickets(res?.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load applications');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const loadDetail = useCallback(async (ticket) => {
    setSelected(ticket);
    if (!ticket) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const d = await fetchCareersTicket(ticket.id);
      setDetail(d);
    } catch (err) {
      setError(err.message || 'Failed to load thread');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const counts = useMemo(() => {
    return STATES.reduce((acc, s) => {
      acc[s.id] = tickets.filter((t) => t.application_state === s.id).length;
      return acc;
    }, {});
  }, [tickets]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tickets.filter((t) => {
      if (t.application_state !== activeTab) return false;
      if (!q) return true;
      return (
        (t.subject || '').toLowerCase().includes(q) ||
        (t.from_email || '').toLowerCase().includes(q) ||
        (t.from_name || '').toLowerCase().includes(q) ||
        (t.role_applied_for || '').toLowerCase().includes(q)
      );
    });
  }, [tickets, activeTab, query]);

  const move = async (nextState) => {
    if (!selected) return;
    setBusy(true);
    const prevList = tickets;
    // Optimistic local update.
    setTickets((rows) => rows.map((r) => r.id === selected.id ? { ...r, application_state: nextState, state_changed_at: new Date().toISOString() } : r));
    setSelected((s) => s ? { ...s, application_state: nextState } : s);
    try {
      const { ticket } = await patchCareersTicket(selected.id, { application_state: nextState });
      setTickets((rows) => rows.map((r) => r.id === ticket.id ? { ...r, ...ticket } : r));
      setSelected(ticket);
      // Refresh detail so state_changed_at reflects.
      const d = await fetchCareersTicket(ticket.id);
      setDetail(d);
    } catch (err) {
      setTickets(prevList);
      setError(`Failed to move application: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Careers</h1>
          <p>
            Applications landing in careers@coynitt.ca — track open · first contact · interview · accepted/declined
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={refresh}>Refresh</button>
      </div>

      {error && (
        <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
          {error}
        </div>
      )}

      {/* State tab bar */}
      <div className="card mb-4">
        <div className="row" data-tabs="true" style={{ gap: 4, borderBottom: '1px solid var(--bd)', marginBottom: 16, flexWrap: 'wrap' }}>
          {STATES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { setActiveTab(s.id); setSelected(null); setDetail(null); }}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                color: activeTab === s.id ? 'var(--teal)' : 'var(--tx2)',
                fontWeight: activeTab === s.id ? 700 : 500,
                fontSize: 13,
                borderBottom: activeTab === s.id ? '2px solid var(--teal)' : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
              }}
            >
              {s.label}
              <span className="badge badge-muted" style={{ marginLeft: 8 }}>{counts[s.id] || 0}</span>
            </button>
          ))}
        </div>

        <div className="row mb-3" style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx3)' }}>
            <Icon name="search" size={16} />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search subject, name, email, role..."
            aria-label="Search applications"
            style={{
              width: '100%',
              padding: '10px 14px 10px 36px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--r-md)',
              color: 'var(--tx1)',
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        {/* List + detail split */}
        <div className="row row-gap-4" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div className="col col-gap-3" style={{ flex: '1 1 360px', minWidth: 320 }}>
            {loading && tickets.length === 0 ? (
              [0, 1, 2, 3].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 64 }} />)
            ) : filtered.length === 0 ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="user" size={26} /></div>
                <h3>No applications in {STATE_BY_ID[activeTab]?.label || activeTab}</h3>
                <p>{tickets.length === 0
                  ? 'No applications received yet. The cron polls careers@coynitt.ca every 5 minutes.'
                  : 'Try clearing the search or switching tabs.'}</p>
              </div>
            ) : (
              filtered.map((t) => (
                <TicketRow
                  key={t.id}
                  ticket={t}
                  selected={selected?.id === t.id}
                  onSelect={loadDetail}
                />
              ))
            )}
          </div>

          <div className="card col col-gap-4" style={{ flex: '2 1 480px', minWidth: 360, padding: 'var(--s5)', background: 'var(--bg-elevated)' }}>
            {!selected ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="folder" size={26} /></div>
                <h3>Select an application</h3>
                <p>Pick a thread from the list to view the full email history and move the application through the funnel.</p>
              </div>
            ) : (
              <>
                <div className="col col-gap-2">
                  <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)' }}>
                    {selected.subject || '(no subject)'}
                  </h2>
                  <div className="text-tx3 text-xs">
                    {selected.from_name ? `${selected.from_name} · ` : ''}{selected.from_email}
                  </div>
                  <div className="row row-gap-2">
                    <span className={clsx('badge', STATE_BY_ID[selected.application_state]?.badge || 'badge-muted')}>
                      {STATE_BY_ID[selected.application_state]?.label || selected.application_state}
                    </span>
                    {selected.role_applied_for && (
                      <span className="badge badge-info">{selected.role_applied_for}</span>
                    )}
                  </div>
                </div>

                <div className="card-divider" />

                <div className="col col-gap-2">
                  <div className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    Move application
                  </div>
                  <StateActions ticket={selected} onMove={move} busy={busy} />
                </div>

                <div className="card-divider" />

                <div className="col col-gap-2">
                  <div className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    Thread ({detail?.messages?.length || 0})
                  </div>
                  {detailLoading ? (
                    <div className="skeleton skeleton-block" style={{ height: 120 }} />
                  ) : !detail?.messages || detail.messages.length === 0 ? (
                    <div className="text-tx3 text-sm">No messages persisted.</div>
                  ) : (
                    detail.messages.map((m) => (
                      <div key={m.id} className="card" style={{ padding: 'var(--s4)', background: 'var(--bg-card)' }}>
                        <div className="row row-gap-3" style={{ justifyContent: 'space-between' }}>
                          <div className="font-semibold text-sm" style={{ color: 'var(--tx1)' }}>
                            {m.from_name || m.from_email}
                          </div>
                          <div className="text-tx3 text-xs" title={fmtAbs(m.received_at)}>
                            {timeAgo(m.received_at)}
                          </div>
                        </div>
                        <div className="text-tx3 text-xs">{m.from_email}</div>
                        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--tx1)', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                          {m.body_text || '(no plain-text body)'}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="card-divider" />

                <div className="row text-tx3 text-xs" style={{ justifyContent: 'space-between' }}>
                  <span>First received: {fmtAbs(selected.first_received)}</span>
                  <span>Last activity: {fmtAbs(selected.last_message_at)}</span>
                </div>
                {selected.state_changed_at && (
                  <div className="text-tx3 text-xs">
                    State last changed: {fmtAbs(selected.state_changed_at)}
                  </div>
                )}

                {detail?.ticket?.from_email && (
                  <a
                    className="btn btn-primary"
                    style={{ alignSelf: 'flex-start', padding: '8px 14px', fontSize: 12 }}
                    href={`mailto:${detail.ticket.from_email}?subject=${encodeURIComponent('Re: ' + (selected.subject || ''))}`}
                  >
                    <Icon name="inbox" size={14} /> Reply via email
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Careers;
